import Moment from 'moment'
import { extendMoment } from 'moment-range'
import settings from '../../components/settings'

const moment = extendMoment(Moment)

const getTimeOptions = (timesData) =>
  timesData.intervals.map((interval) => {
    const start = interval.from.substr(0, 5)
    return {
      value: start,
      label: start,
      disabled: interval.type === 'reserved',
    }
  })

const getLengthOptions = (timesData, selectedTime) => {
  const lengthOptions = []
  const day = '1970-01-01'
  const timeframe = settings.timeframe
  let temp = timeframe
  while (temp <= 180) {
    let disabled = false
    const potentialStart = moment(`${day} ${selectedTime}`)
    const potentialEnd = moment(`${day} ${selectedTime}`).add(temp, 'minutes')
    const potentialRange = moment.range(potentialStart, potentialEnd)
    const dayEnd = moment(`${day} ${timesData.intervals.at(-1).end}`)

    if (potentialEnd > dayEnd) {
      disabled = true
    }

    const reservedIntervals = timesData.intervals.filter(
      (interval) => interval.type === 'reserved'
    )

    if (reservedIntervals.length > 0) {
      let tempStart = reservedIntervals[0].from
      let tempEnd = null
      const reservedRanges = []
      reservedIntervals.forEach((interval, index) => {
        const nextInterval = reservedIntervals.find(
          (i) => i.from === interval.to
        )
        if (!nextInterval) {
          tempEnd = interval.to
          reservedRanges.push({ from: tempStart, to: tempEnd })
          if (index + 1 < reservedIntervals.length) {
            tempStart = reservedIntervals[index + 1].from
          }
        }
      })

      reservedRanges.forEach((range) => {
        const momentRange = moment.range(
          moment(`${day} ${range.from}`),
          moment(`${day} ${range.to}`)
        )
        if (momentRange.contains(potentialEnd, { exclusive: true })) {
          disabled = true
        }
        if (potentialRange.overlaps(momentRange)) {
          disabled = true
        }
      })

      if (
        potentialEnd.isAfter(moment(`${day} ${timesData.intervals.at(-1).to}`))
      ) {
        disabled = true
      }
    }

    lengthOptions.push({ value: temp, label: `${temp} mins`, disabled })
    temp += timeframe
  }
  return lengthOptions
}

export const checkEmpty = (value, returnEmptyString) => {
  const returnValue = returnEmptyString ? '' : '-'
  if (!value) return returnValue
  if (value.length === 0) return returnValue
  return value
}

export const getNewsletterConsent = (newsletterConsent, translateFunc) => {
  if (!newsletterConsent) return translateFunc('no')
  return translateFunc('yes')
}

// TODO: Remove this when we confirm that subsystems are working properly
export const getServiceId = ({
  externalMeetingProvider,
  subsystem,
  serviceId,
}) => {
  let videoServiceId = settings.videoServiceId
  let storeServiceId = settings.storeServiceId
  let discoverServiceId = settings.discoverServiceId
  if (subsystem) {
    const mapping = settings.serviceMappings.find(
      (s) => s.subsystem === subsystem
    )
    if (mapping) {
      const videoService = mapping.services.find(
        (s) => s.from === videoServiceId
      )
      const storeService = mapping.services.find(
        (s) => s.from === storeServiceId
      )
      const discoverService = mapping.services.find(
        (s) => s.from === discoverServiceId
      )
      videoServiceId = videoService.to
      storeServiceId = storeService.to
      discoverServiceId = discoverService.to
    }
  } else {
    return serviceId
  }

  if (externalMeetingProvider === 'videoslot-appointment') {
    return videoServiceId
  }
  if (externalMeetingProvider === 'discoverslot-appointment') {
    return discoverServiceId
  }
  if (externalMeetingProvider === 'simplybookme-appointment') {
    return storeServiceId
  }
  return null
}

export const lengthFromEndTime = (startTime, endTime) => {
  const minutes = moment(`1970-01-01 ${endTime}`).diff(
    moment(`1970-01-01 ${startTime}`),
    'minutes'
  )
  return minutes
}

export const endTimeFromLength = (startTime, minutes) => {
  if (!minutes || minutes === '') {
    return null
  }
  const endTime = moment(`1970-01-01 ${startTime}`)
    .add(minutes, 'minutes')
    .format('HH:mm')
  return endTime
}

export const getOptions = ({
  storesData,
  services,
  serviceProvidersData,
  timesData,
  serviceProviderId,
  selectedTime,
}) => {
  const allowedServiceIds =
    serviceProvidersData?.data?.find((s) => s.id === serviceProviderId)
      ?.serviceIds || []
  const serviceOptions = [
    { value: '', label: '', disabled: false },
    ...[
      ...(services?.map((i) => ({
        value: i.id,
        label: i.heading || '',
        disabled: !allowedServiceIds?.includes(i.id),
      })) || []),
    ].sort((a, b) => a.label.localeCompare(b.label)),
  ]

  const serviceProviderOptions = [
    { value: '', label: '', disabled: false },
    ...[
      ...(serviceProvidersData?.data?.map((i) => ({
        value: i.id,
        label: i.name,
      })) || []),
    ].sort((a, b) => a.label.localeCompare(b.label)),
  ]

  const storeOptions = [...(storesData?.data || [])]
    .sort((s1, s2) => {
      if (s1.storeName > s2.storeName) return 1
      if (s1.storeName < s2.storeName) return -1
      return 0
    })
    .map((i) => ({
      value: i.id,
      label: i.storeName,
      disabled: i.externalMeetingData.meetingData.simplyBookMeId === '0',
    }))

  let timeOptions = []
  let lengthOptions = []
  let closed = false
  if (timesData?.intervals?.length > 0) {
    timeOptions = getTimeOptions(timesData)
    lengthOptions = getLengthOptions(timesData, selectedTime)
  } else {
    closed = true
  }

  timeOptions.unshift({ value: '', label: '', disabled: false })
  lengthOptions.unshift({ value: '', label: '', disabled: false })

  return {
    timesData,
    storeOptions,
    serviceOptions,
    serviceProviderOptions,
    timeOptions,
    lengthOptions,
    closed,
  }
}

export const getLocationId = (storeId, storesData) => {
  const simplyBookMeId = storesData?.stores?.stores?.find(
    (s) => parseInt(s.id, 10) === parseInt(storeId, 10)
  )?.externalMeetingData?.meetingData?.simplyBookMeId

  return simplyBookMeId ? parseInt(simplyBookMeId, 10) : null
}

export const checkMeetingAllowUpdate = (meetingDate, meetingTime) => {
  const now = new Date(new Date().getTime() - 24 * 60 * 60 * 1000)
  const timeAndDate = new Date(`${meetingDate}T${meetingTime}`)
  return now < timeAndDate
}

const hasMeetingPassed = (meetingDate, meetingTime, daysOffset = 0) => {
  const now = new Date()
  now.setDate(now.getDate() + daysOffset) // can be 0, -1, +x, etc.
  const meetingDateTime = moment(`${meetingDate} ${meetingTime}`)
  return now > meetingDateTime.toDate()
}

export const checkMeetingAllowCancel = (meetingDate, meetingTime) =>
  !hasMeetingPassed(meetingDate, meetingTime, -1)

export const checkMeetingHasPassed = (meetingDate, meetingTime) =>
  hasMeetingPassed(meetingDate, meetingTime, 0)

const LEGACY_CANCELLATION_REASON_KEYS = {
  NoLongerAvailable: { customer: 'reason1', store: 'store-reason1' },
  ChangedMind: { customer: 'reason2', store: 'store-reason2' },
  WentElsewhere: { customer: 'reason3', store: 'store-reason3' },
}

/**
 * Formats cancellationReason for appointment detail display.
 * Legacy cancel flow stores enum values; new flow uses pipe-separated segments.
 */
export const formatCancellationReasonDisplay = (
  cancellationReason,
  cancelledBy,
  translateCancelStep2
) => {
  if (!cancellationReason || !String(cancellationReason).trim()) {
    return null
  }

  const trimmed = String(cancellationReason).trim()
  const legacyMapping = LEGACY_CANCELLATION_REASON_KEYS[trimmed]

  if (legacyMapping) {
    const messageKey =
      cancelledBy === 'store' ? legacyMapping.store : legacyMapping.customer
    const label = translateCancelStep2(messageKey)
    return label || trimmed
  }

  if (trimmed.includes('|')) {
    return trimmed
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean)
      .join('\n')
  }

  return trimmed
}
